import {
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  GlobalStyles,
  TablePagination,
} from "@mui/material";
import { useIntl } from "react-intl";
import { numericFormatter } from "react-number-format";
import MDBox from "components/MDBox";
import ForwardIcon from "@mui/icons-material/Forward";

interface Kardex {
  id: number;
  estatus: number;
}

interface DetalleTipoProductoProps {
  verKardex?: Kardex[] | null;
  kardexPaginado: Kardex[];
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

const KardexModulo = ({
  verKardex,
  kardexPaginado,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
}: DetalleTipoProductoProps) => {
  if (!verKardex) return null;
  const intl = useIntl();

  const getEstado = (estado: number) => {
    switch (estado) {
      case 1:
        return "success";
      case 0:
        return "error";
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    1: "ACTIVADO",
    0: "DESACTIVADO",
  };

  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[estado] ?? "SIN ESTADO";
  };

  const renderTipoMovimientoChip = (tipo_movimiento: number) => {
    if (tipo_movimiento === 1) {
      return (
        <Chip
          label="Entrada"
          sx={{
            backgroundColor: "#31ad39",
            color: "#fff",
          }}
          variant="filled"
        />
      );
    }

    if (tipo_movimiento === 2) {
      return (
        <Chip
          label="Salida"
          sx={{
            backgroundColor: "#c40c1e",
            color: "#fff",
          }}
          variant="filled"
        />
      );
    }

    return null;
  };

  const renderTipoMovimientoIcon = (tipo_movimiento: number) => {
    if (tipo_movimiento === 1) {
      //Entrada
      return <ForwardIcon fontSize="large" color="success" />;
    }
    if (tipo_movimiento === 2) {
      //Salida
      return <ForwardIcon fontSize="large" color="error" sx={{ transform: "rotate(180deg)" }} />;
    }

    return null;
  };

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={3}>
        {/* Información de la talla */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Grid container>
              <Grid item xs={12} md={12}>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{ color: "#004c93", textAlign: { xs: "center", md: "left" } }}
                >
                  Movimientos en inventario registrados
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ m: 2 }} />
            {verKardex.length > 0 ? (
              <>
                {verKardex.map((k: any, key: number) => (
                  <>
                    <Grid container spacing={2} key={key}>
                      <Grid
                        item
                        xs={2}
                        md={1}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                        mt={2}
                      >
                        {renderTipoMovimientoIcon(k.tipo_movimiento.id)}
                      </Grid>
                      <Grid item xs={5} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Movimiento
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {renderTipoMovimientoChip(k.tipo_movimiento.id)}
                        </Typography>
                      </Grid>
                      <Grid item xs={5} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Producto
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {k?.inventario.nombre}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Requisición
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {k?.id_requisicion}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {k?.cantidad}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad anterior
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {k?.cantidad_anterior}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad nueva
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {k?.cantidad_nueva}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Precio
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {numericFormatter(k?.costo_unitario + "", {
                            thousandSeparator: ",",
                            decimalScale: 2,
                            fixedDecimalScale: false,
                            prefix: "$",
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Fecha
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {new Date(k?.created_at).toLocaleDateString("es-MX")}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ m: 1 }} />
                  </>
                ))}

                <MDBox
                  mt={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    justifyContent: "center",
                  }}
                >
                  <GlobalStyles
                    styles={{
                      ".MuiTablePagination-select.MuiSelect-standard": {
                        color: "#000 !important",
                        WebkitTextFillColor: "#000 !important",
                      },
                    }}
                  />
                  <TablePagination
                    component="div"
                    count={kardexPaginado?.length ?? 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[3, 6, 10, 15]}
                    labelRowsPerPage="Movimientos por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      color: "#000",
                      direction: "ltr",
                      ".MuiTablePagination-toolbar": {
                        justifyContent: "space-between",
                        padding: "16px 24px",
                      },
                      ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        margin: 0,
                      },
                      ".MuiTablePagination-actions svg": {
                        transform: "scaleX(-1)",
                      },
                    }}
                  />
                </MDBox>
              </>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({ id: "no_hay_datos_registrados" })}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default KardexModulo;
